import {DataStatus} from 'platform/components';

import {isEmpty, isNil, isNotNil} from 'ramda';
import {isString} from 'ramda-adjunct';

import {
  type AbcXyzAnalysisDetailResponse,
  useGetAbcXyzAnalysisDetailQuery,
} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AbcXyzRowDetailContent} from './AbcXyzRowDetailContent';

const NOT_FOUND_STATUS = 404;

interface AbcXyzRowDetailDialogProps extends RequiredTestIdProps {
  /** Id of the clicked grid row, which is also the report row's own id. */
  id: string;
}

/**
 * Without the article the detail grids would have nothing to filter by, and a grid without that
 * filter returns the rows of every article. So a detail without one is not rendered.
 */
const withArticleId = (detail: AbcXyzAnalysisDetailResponse | Nullish) => {
  const articleId = detail?.articleId;

  return isNotNil(detail) && isString(articleId) && !isEmpty(articleId)
    ? {...detail, articleId}
    : undefined;
};

export function AbcXyzRowDetailDialog(props: AbcXyzRowDetailDialogProps) {
  const {
    data: detail,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
    refetch,
  } = useGetAbcXyzAnalysisDetailQuery(props.id);

  const detailWithArticle = withArticleId(detail);
  // Only a settled request can say there is no article. While a retry runs after a failure the
  // query is neither loading nor in error and has no data yet, which is not "not found".
  const isNotFound =
    (isError && 'status' in error && error.status === NOT_FOUND_STATUS) ||
    (isSuccess && isNil(detailWithArticle));

  return (
    <DataStatus
      data={detailWithArticle}
      isLoading={isLoading || (isFetching && isNil(detail))}
      isError={isError || isNotFound}
      errorMessage={
        isNotFound
          ? i18n.t('entity.warehouse.reporting.abcXyzRowNotFound')
          : i18n.t('general.labels.somethingWentWrong')
      }
      errorAction={
        isNotFound
          ? undefined
          : {
              title: i18n.t('general.actions.retry'),
              isLoading: isFetching,
              onClick: () => refetch(),
            }
      }
      minHeight="100%"
      data-testid={suffixTestId('status', props)}
    >
      {({data}) => (
        <AbcXyzRowDetailContent
          detail={data}
          articleId={data.articleId}
          data-testid={props['data-testid']}
        />
      )}
    </DataStatus>
  );
}
